// Module
var code = `<section class="page-body default-content">

  <h2>Typography</h2>
  <p>
    Typography is the foundation of the SpyneJS design system. It provides consistent
    text styles for headings, paragraphs, and supporting text. Font sizes are responsive
    with <code>clamp()</code>, ensuring readability across devices.
  </p>

  <!-- Specimen -->
  <h3>Font Scale Specimen</h3>
  <p>The full responsive font size scale from <code>xxs</code> to <code>9xl</code>.</p>
  <div class="typography-examples">
    <p style="font-size: var(--font-size-xxs);">XXS – font-size-xxs</p>
    <p style="font-size: var(--font-size-xs);">XS – font-size-xs</p>
    <p style="font-size: var(--font-size-sm);">SM – font-size-sm</p>
    <p style="font-size: var(--font-size-base);">Base – font-size-base</p>
    <p style="font-size: var(--font-size-lg);">LG – font-size-lg</p>
    <p style="font-size: var(--font-size-xl);">XL – font-size-xl</p>
    <p style="font-size: var(--font-size-2xl);">2XL – font-size-2xl</p>
    <p style="font-size: var(--font-size-3xl);">3XL – font-size-3xl</p>
    <p style="font-size: var(--font-size-4xl);">4XL – font-size-4xl</p>
    <p style="font-size: var(--font-size-5xl);">5XL – font-size-5xl</p>
    <p style="font-size: var(--font-size-6xl);">6XL – font-size-6xl</p>
    <p style="font-size: var(--font-size-7xl);">7XL – font-size-7xl</p>
    <p style="font-size: var(--font-size-8xl);">8XL – font-size-8xl</p>
    <p style="font-size: var(--font-size-9xl);">9XL – font-size-9xl</p>
  </div>
  <div class="codeblock">
    <div class="codeblock__header">
      <button class="codeblock__copy" data-copy-btn>Copy</button>
    </div>
    <pre><code class="language-html">
&lt;p class="text-xxs"&gt;XXS – font-size-xxs&lt;/p&gt;
&lt;p class="text-xs"&gt;XS – font-size-xs&lt;/p&gt;
&lt;p class="text-sm"&gt;SM – font-size-sm&lt;/p&gt;
&lt;p&gt;Base – font-size-base&lt;/p&gt;
&lt;p class="text-lg"&gt;LG – font-size-lg&lt;/p&gt;
&lt;p class="text-xl"&gt;XL – font-size-xl&lt;/p&gt;
&lt;p class="text-2xl"&gt;2XL – font-size-2xl&lt;/p&gt;
&lt;p class="text-3xl"&gt;3XL – font-size-3xl&lt;/p&gt;
&lt;p class="text-4xl"&gt;4XL – font-size-4xl&lt;/p&gt;
&lt;p class="text-5xl"&gt;5XL – font-size-5xl&lt;/p&gt;
&lt;p class="text-6xl"&gt;6XL – font-size-6xl&lt;/p&gt;
&lt;p class="text-7xl"&gt;7XL – font-size-7xl&lt;/p&gt;
&lt;p class="text-8xl"&gt;8XL – font-size-8xl&lt;/p&gt;
&lt;p class="text-9xl"&gt;9XL – font-size-9xl&lt;/p&gt;
    </code></pre>
  </div>

  <!-- Headings -->
  <h3>Headings</h3>
  <p>Use heading classes (<code>.h1</code>, <code>.h2</code>, <code>.h3</code>, etc.) for clear hierarchy.</p>
  <div class="typography-examples">
    <h1>Heading 1</h1>
    <h2>Heading 2</h2>
    <h3>Heading 3</h3>
  </div>
  <div class="codeblock">
    <div class="codeblock__header">
      <button class="codeblock__copy" data-copy-btn>Copy</button>
    </div>
    <pre><code class="language-html">
&lt;h1&gt;Heading 1&lt;/h1&gt;
&lt;h2&gt;Heading 2&lt;/h2&gt;
&lt;h3&gt;Heading 3&lt;/h3&gt;
    </code></pre>
  </div>

  <!-- Paragraphs -->
  <h3>Paragraph</h3>
  <p>Paragraph text uses the base font size and relaxed line height for readability.</p>
  <div class="typography-examples">
    <p>
      This is an example paragraph. SpyneJS typography ensures consistent spacing,
      font sizing, and line height for optimal reading experience.
    </p>
  </div>
  <div class="codeblock">
    <div class="codeblock__header">
      <button class="codeblock__copy" data-copy-btn>Copy</button>
    </div>
    <pre><code class="language-html">
&lt;p&gt;
  This is an example paragraph. SpyneJS typography ensures consistent spacing,
  font sizing, and line height for optimal reading experience.
&lt;/p&gt;
    </code></pre>
  </div>

  <!-- Small / Muted -->
  <h3>Small Text</h3>
  <p>Use <code>&lt;small&gt;</code> or <code>.text-sm</code> for secondary or muted text.</p>
  <div class="typography-examples">
    <small>Muted caption or note text</small>
  </div>
  <div class="codeblock">
    <div class="codeblock__header">
      <button class="codeblock__copy" data-copy-btn>Copy</button>
    </div>
    <pre><code class="language-html">
&lt;small&gt;Muted caption or note text&lt;/small&gt;
    </code></pre>
  </div>

  <!-- Font Weights -->
  <h3>Font Weights</h3>
  <p>Use utility classes or semantic tags to apply font weights from the design system.</p>
  <div class="typography-examples">
    <p style="font-weight: 400;">Normal weight (400)</p>
    <p style="font-weight: 600;">Semibold weight (600)</p>
    <p style="font-weight: 700;">Bold weight (700)</p>
  </div>
  <div class="codeblock">
    <div class="codeblock__header">
      <button class="codeblock__copy" data-copy-btn>Copy</button>
    </div>
    <pre><code class="language-html">
&lt;p style="font-weight: 400;"&gt;Normal weight (400)&lt;/p&gt;
&lt;p style="font-weight: 600;"&gt;Semibold weight (600)&lt;/p&gt;
&lt;p style="font-weight: 700;"&gt;Bold weight (700)&lt;/p&gt;
    </code></pre>
  </div>

  <!-- Accessibility -->
  <h3>Accessibility</h3>
  <ul>
    <li>Maintain a minimum body size of <code>1rem</code> (16px) for readability.</li>
    <li>Headings use responsive <code>clamp()</code> to adapt across viewports.</li>
    <li>Use semantic HTML tags (<code>&lt;h1&gt;</code>, <code>&lt;p&gt;</code>, <code>&lt;small&gt;</code>) to support screen readers.</li>
  </ul>

</section>
`;
// Exports
module.exports = code;